import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { services, places, placeOf } from '../data/common';
import {
  MAX_TRAVELLERS, ageLimits, agesFor, annualTripLengths, destinationCountries, destinationName, destinationsFor,
  isRegion, journeys, parseInsuranceQuery, popularCountries, regions, searchProblem, studentCover, toInsuranceParams,
} from '../../modules/insurance/searchModel';
import {
  MAX_PASSENGERS, journeys as transferJourneys, parseTransferQuery, searchProblem as transferProblem, transferQueryString,
} from '../../modules/transfers/searchModel';
import { searchPlaces } from '../../api/transfers';
import {
  MAX_ADULTS, MAX_CHILDREN, MAX_ROOMS, hotelQueryString, parseHotelQuery, parseRooms, roomsLabel, roomsParam,
  searchProblem as hotelProblem,
} from '../../modules/hotels/searchModel';
import { searchDestinations } from '../../api/hotels';
import { busQueryString, parseBusQuery, searchProblem as busProblem } from '../../modules/bus/searchModel';
import { searchStations } from '../../api/bus';
import { countries } from '../data/countries';
import Icon from './Icons';
import Calendar, { MonthCalendar } from './Calendar';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return iso(d); };
const todayIso = iso(new Date());

const parseIso = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d || 1); };
const fmtDate = (s) => {
  if (!s) return null;
  const d = parseIso(s);
  return {
    day: d.getDate(),
    month: d.toLocaleString('en-US', { month: 'short' }) + "'" + String(d.getFullYear()).slice(2),
    weekday: d.toLocaleString('en-US', { weekday: 'long' }),
  };
};

const defaults = {
  flights: { trip: 'oneway', from: 'Delhi', to: 'Mumbai', date: addDays(7), returnDate: '', adults: 1, children: 0, infants: 0, cabin: 'Economy', fare: 'Regular' },
  // Kept as URL strings; see modules/hotels/searchModel.js. dest is the destination key from the suggestions.
  hotels: { dest: 'GOA, INDIA', name: 'Goa, India', checkin: addDays(7), checkout: addDays(9), rooms: '2', nat: 'IN' },
  packages: { from: 'Delhi', destination: '', month: '', adults: 2, children: 0 },
  sightseeing: { city: 'Dubai', date: addDays(7), adults: 2, children: 0 },
  // Kept as URL strings; see modules/transfers/searchModel.js. pickupId / dropId are place ids from the suggestions.
  transfers: { journey: 'airport', pickup: '', pickupId: '', drop: '', dropId: '', date: addDays(7), time: '10:00', returnDate: '', returnTime: '18:00', passengers: 2 },
  // Kept as URL strings; see modules/insurance/searchModel.js.
  insurance: { journey: 'single', dest: 'ASI', start: addDays(7), end: addDays(14), cover: '365', maxTrip: '45', ages: '30' },
  // Station names from the bus suggestions; see modules/bus/searchModel.js.
  bus: { from: 'Hyderabad', to: 'Bangalore', date: addDays(1) },
};
const numericKeys = ['adults', 'children', 'infants', 'passengers', 'travellers'];

function usePopover() {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return { ref, open, setOpen };
}

function Segment({ label, children, onClick, active, className = '', innerRef }) {
  return (
    <div ref={innerRef} className={`seg ${active ? 'active' : ''} ${className}`} onClick={onClick}>
      <span className="seg-label">{label} <Icon name="down" size={12} strokeWidth={2.5} /></span>
      {children}
    </div>
  );
}

function CitySegment({ label, value, onChange, allowAny, mode = 'airport', extra }) {
  const { ref, open, setOpen } = usePopover();
  const [term, setTerm] = useState('');
  const p = placeOf(value);
  const list = places.filter((x) => (x.city + x.code + x.country + x.airport).toLowerCase().includes(term.toLowerCase()));
  const pick = (v) => { onChange(v); setOpen(false); setTerm(''); };
  return (
    <Segment label={label} innerRef={ref} active={open} onClick={() => setOpen(true)}>
      <span className="seg-value">{value || 'Anywhere'}</span>
      <span className="seg-sub">{value ? (mode === 'airport' ? `${p.code}, ${p.airport}` : p.country) : 'All destinations'}</span>
      {extra}
      {open && (
        <div className="popover city-pop" onClick={(e) => e.stopPropagation()}>
          <div className="pop-search">
            <Icon name="search" size={16} />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder={mode === 'airport' ? 'Search city or airport' : 'Search city or country'} />
          </div>
          <div className="pop-title">{term ? 'Results' : 'Popular cities'}</div>
          <ul className="pop-list">
            {allowAny && !term && (
              <li onClick={() => pick('')}><Icon name="globe" size={18} /><div><b>Anywhere</b><span>All destinations</span></div></li>
            )}
            {list.map((x) => (
              <li key={x.city} className={x.city === value ? 'selected' : ''} onClick={() => pick(x.city)}>
                <Icon name={mode === 'airport' ? 'flights' : 'pin'} size={18} />
                <div><b>{x.city}, {x.country}</b><span>{mode === 'airport' ? x.airport : `${x.code} · ${x.country}`}</span></div>
                {mode === 'airport' && <em>{x.code}</em>}
              </li>
            ))}
            {!list.length && <li className="pop-empty">No matching cities</li>}
          </ul>
        </div>
      )}
    </Segment>
  );
}

function DateSegment({ label, value, onChange, min = todayIso, type = 'date', emptyText = 'Select date', onClear, range }) {
  const { ref, open, setOpen } = usePopover();
  const minIso = typeof min === 'function' ? min() : min;
  const pick = (next) => { onChange(next); setOpen(false); };
  // The two-month calendar is wide: shift it left when it would run off the right edge of the screen.
  const pop = useRef(null);
  useLayoutEffect(() => {
    const el = pop.current;
    if (!open || !el) return;
    el.style.left = '';
    const overflow = el.getBoundingClientRect().right - (window.innerWidth - 12);
    if (overflow > 0) el.style.left = `${-overflow}px`;
  }, [open]);
  let body;
  if (type === 'month') {
    const d = value ? parseIso(value + '-01') : null;
    body = d ? (
      <><span className="seg-value">{d.toLocaleString('en-US', { month: 'long' })}</span><span className="seg-sub">{d.getFullYear()}</span></>
    ) : <><span className="seg-value muted-value">Any month</span><span className="seg-sub">Flexible dates</span></>;
  } else {
    const f = fmtDate(value);
    body = f ? (
      <><span className="seg-value"><big>{f.day}</big> {f.month}</span><span className="seg-sub">{f.weekday}</span></>
    ) : <><span className="seg-value muted-value">{emptyText}</span><span className="seg-sub">Tap to add</span></>;
  }
  return (
    <Segment label={label} innerRef={ref} active={open} onClick={() => setOpen(true)}>
      {body}
      {value && onClear && <button type="button" className="seg-clear" onClick={(e) => { e.stopPropagation(); onClear(); setOpen(false); }} aria-label="Clear">×</button>}
      {/* Kept for forms and assistive tech; the popover below is the visible picker. */}
      <input className="seg-native" type={type} value={value} min={type === 'month' ? minIso.slice(0, 7) : minIso}
        onChange={(e) => onChange(e.target.value)} tabIndex={-1} aria-label={label} />
      {open && (
        <div ref={pop} className={`popover cal-pop ${type === 'month' ? 'cal-pop-month' : ''}`} role="dialog" aria-label={`Choose ${label.toLowerCase()}`}
          onClick={(e) => e.stopPropagation()}>
          <div className="cal-pop-head">
            <b>{label}</b>
            {value && onClear && <button type="button" className="link small" onClick={() => { onClear(); setOpen(false); }}>Clear</button>}
          </div>
          {type === 'month'
            ? <MonthCalendar value={value} min={minIso} onSelect={pick} />
            : <Calendar value={value} min={minIso} range={range} onSelect={pick} />}
        </div>
      )}
    </Segment>
  );
}

function Counter({ label, hint, value, min, max, onChange }) {
  return (
    <div className="counter">
      <div><b>{label}</b>{hint && <span>{hint}</span>}</div>
      <div className="counter-ctl">
        <button type="button" disabled={value <= min} onClick={() => onChange(value - 1)}>−</button>
        <span>{value}</span>
        <button type="button" disabled={value >= max} onClick={() => onChange(value + 1)}>+</button>
      </div>
    </div>
  );
}

function PaxSegment({ label, value, sub, counters, values, set, cabins }) {
  const { ref, open, setOpen } = usePopover();
  return (
    <Segment label={label} innerRef={ref} active={open} onClick={() => setOpen(true)} className="seg-pax">
      <span className="seg-value">{value}</span>
      <span className="seg-sub">{sub}</span>
      {open && (
        <div className="popover pax-pop" onClick={(e) => e.stopPropagation()}>
          {counters.map((c) => (
            <Counter key={c.key} {...c} value={values[c.key]} onChange={set(c.key)} />
          ))}
          {cabins && (
            <>
              <div className="pop-title">Travel class</div>
              <div className="cabin-chips">
                {cabins.map((c) => (
                  <button type="button" key={c} className={`chip ${values.cabin === c ? 'active' : ''}`} onClick={() => set('cabin')(c)}>{c}</button>
                ))}
              </div>
            </>
          )}
          <button type="button" className="btn btn-primary block" onClick={() => setOpen(false)}>Apply</button>
        </div>
      )}
    </Segment>
  );
}

function InputSegment({ label, value, onChange, placeholder, sub, type = 'text' }) {
  const input = useRef(null);
  return (
    <Segment label={label} onClick={() => input.current.focus()}>
      <input ref={input} className="seg-input" type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      <span className="seg-sub">{sub}</span>
    </Segment>
  );
}

/**
 * Address box with live suggestions from the transfers API. Typing keeps free text (looked up by
 * name when searching); picking a suggestion also stores its place id.
 */
function PlaceSegment({ label, name, placeId, onChange, placeholder }) {
  const { ref, open, setOpen } = usePopover();
  const [term, setTerm] = useState('');
  const [state, setState] = useState({ loading: false, places: [], error: '' });
  const needle = term.trim();

  useEffect(() => {
    if (!open || needle.length < 2) {
      setState({ loading: false, places: [], error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: '' }));
    const timer = setTimeout(() => {
      searchPlaces(needle, controller.signal)
        .then((places) => setState({ loading: false, places, error: '' }))
        .catch((error) => { if (error.name !== 'AbortError') setState({ loading: false, places: [], error: 'Address search is unavailable right now.' }); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [open, needle]);

  const [main, ...rest] = (name || '').split(',');
  const pick = (place) => { onChange({ name: place.name, placeId: place.placeId }); setOpen(false); setTerm(''); };
  const keepTyped = () => { if (needle) onChange({ name: needle, placeId: '' }); setOpen(false); setTerm(''); };

  return (
    <Segment label={label} innerRef={ref} active={open} onClick={() => { setTerm(name || ''); setOpen(true); }} className="seg-place">
      <span className={`seg-value ${name ? '' : 'muted-value'}`}>{main || placeholder}</span>
      <span className="seg-sub">{rest.join(',').trim() || (placeId ? 'Selected place' : 'Airport, hotel or address')}</span>
      {open && (
        <div className="popover city-pop" onClick={(e) => e.stopPropagation()}>
          <div className="pop-search">
            <Icon name="search" size={16} />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search airport, hotel or address" aria-label={`${label} search`}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (state.places[0]) pick(state.places[0]); else keepTyped();
              }} />
          </div>
          <ul className="pop-list">
            {needle.length < 2 && <li className="pop-empty">Type at least 2 letters</li>}
            {needle.length >= 2 && state.loading && !state.places.length && <li className="pop-empty">Searching…</li>}
            {state.error && <li className="pop-empty">{state.error}</li>}
            {state.places.map((p) => {
              const [first, ...others] = p.name.split(',');
              return (
                <li key={p.placeId} className={p.placeId === placeId ? 'selected' : ''} onClick={() => pick(p)}>
                  <Icon name={/airport/i.test(p.name) ? 'flights' : 'pin'} size={18} />
                  <div><b>{first}</b><span>{others.join(',').trim()}</span></div>
                </li>
              );
            })}
            {needle.length >= 2 && !state.loading && !state.error && !state.places.length && <li className="pop-empty">No matching places</li>}
          </ul>
        </div>
      )}
    </Segment>
  );
}

/** Hotel destination with live suggestions from the hotels API. Picking one stores its key and name. */
function HotelDestinationSegment({ name, onChange }) {
  const { ref, open, setOpen } = usePopover();
  const [term, setTerm] = useState('');
  const [state, setState] = useState({ loading: false, list: [], error: '' });
  const needle = term.trim();

  useEffect(() => {
    if (!open || needle.length < 2) {
      setState({ loading: false, list: [], error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: '' }));
    const timer = setTimeout(() => {
      searchDestinations(needle, controller.signal)
        .then((list) => setState({ loading: false, list, error: '' }))
        .catch((error) => { if (error.name !== 'AbortError') setState({ loading: false, list: [], error: 'Destination search is unavailable right now.' }); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [open, needle]);

  const [main, ...rest] = (name || '').split(',');
  const pick = (d) => { onChange({ dest: d.key, name: d.fullName }); setOpen(false); setTerm(''); };

  return (
    <Segment label="City, area or destination" innerRef={ref} active={open} onClick={() => { setTerm(main?.trim() || ''); setOpen(true); }} className="seg-place">
      <span className={`seg-value ${name ? '' : 'muted-value'}`}>{main || 'Where to?'}</span>
      <span className="seg-sub">{rest.join(',').trim() || 'City or area'}</span>
      {open && (
        <div className="popover city-pop" onClick={(e) => e.stopPropagation()}>
          <div className="pop-search">
            <Icon name="search" size={16} />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search city or area" aria-label="Destination search"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (state.list[0]) pick(state.list[0]); } }} />
          </div>
          <ul className="pop-list">
            {needle.length < 2 && <li className="pop-empty">Type at least 2 letters</li>}
            {needle.length >= 2 && state.loading && !state.list.length && <li className="pop-empty">Searching…</li>}
            {state.error && <li className="pop-empty">{state.error}</li>}
            {state.list.map((d) => {
              const [first, ...others] = d.fullName.split(',');
              return (
                <li key={d.key} onClick={() => pick(d)}>
                  <Icon name="pin" size={18} />
                  <div><b>{first}</b><span>{others.join(',').trim()}</span></div>
                  {d.hotelCount > 0 && <em>{d.hotelCount.toLocaleString('en-IN')} hotels</em>}
                </li>
              );
            })}
            {needle.length >= 2 && !state.loading && !state.error && !state.list.length && <li className="pop-empty">No matching destinations</li>}
          </ul>
        </div>
      )}
    </Segment>
  );
}

/** Bus city or stop with live suggestions from the bus API. Picking one stores the station name the API searches by. */
function StationSegment({ label, value, onChange, extra }) {
  const { ref, open, setOpen } = usePopover();
  const [term, setTerm] = useState('');
  const [state, setState] = useState({ loading: false, list: [], error: '' });
  const needle = term.trim();

  useEffect(() => {
    if (!open || needle.length < 2) {
      setState({ loading: false, list: [], error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: '' }));
    const timer = setTimeout(() => {
      searchStations(needle, controller.signal)
        .then((list) => setState({ loading: false, list, error: '' }))
        .catch((error) => { if (error.name !== 'AbortError') setState({ loading: false, list: [], error: 'City search is unavailable right now.' }); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [open, needle]);

  const pick = (name) => { onChange(name); setOpen(false); setTerm(''); };

  return (
    <Segment label={label} innerRef={ref} active={open} onClick={() => { setTerm(''); setOpen(true); }} className="seg-place">
      <span className={`seg-value ${value ? '' : 'muted-value'}`}>{value || 'Select city'}</span>
      <span className="seg-sub">{value ? 'City or stop' : 'Type to search'}</span>
      {extra}
      {open && (
        <div className="popover city-pop" onClick={(e) => e.stopPropagation()}>
          <div className="pop-search">
            <Icon name="search" size={16} />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search city or stop" aria-label={`${label} search`}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (state.list[0]) pick(state.list[0].name); } }} />
          </div>
          <ul className="pop-list">
            {needle.length < 2 && <li className="pop-empty">Type at least 2 letters</li>}
            {needle.length >= 2 && state.loading && !state.list.length && <li className="pop-empty">Searching…</li>}
            {state.error && <li className="pop-empty">{state.error}</li>}
            {state.list.map((s) => (
              <li key={s.name} className={s.name === value ? 'selected' : ''} onClick={() => pick(s.name)}>
                <Icon name="bus" size={18} /><div><b>{s.name}</b></div>
              </li>
            ))}
            {needle.length >= 2 && !state.loading && !state.error && !state.list.length && <li className="pop-empty">No matching cities</li>}
          </ul>
        </div>
      )}
    </Segment>
  );
}

/** Hotel rooms: adults and each child's age per room, plus the lead guest's nationality. Value is the URL rooms string. */
function RoomsSegment({ value, nat, onChange, onNat }) {
  const { ref, open, setOpen } = usePopover();
  const rooms = parseRooms(value);
  const update = (i, patch) => onChange(roomsParam(rooms.map((r, j) => (j === i ? { ...r, ...patch } : r))));
  const setChildren = (i, n) => update(i, { childAges: Array.from({ length: n }, (_, k) => rooms[i].childAges[k] ?? 5) });
  return (
    <Segment label="Rooms & Guests" innerRef={ref} active={open} onClick={() => setOpen(true)} className="seg-pax">
      <span className="seg-value">{roomsLabel(rooms).split(' · ')[0]}</span>
      <span className="seg-sub">{roomsLabel(rooms).split(' · ')[1]}</span>
      {open && (
        <div className="popover pax-pop rooms-pop" onClick={(e) => e.stopPropagation()}>
          {rooms.map((room, i) => (
            <div key={i} className="room-pick">
              <div className="room-pick-head">
                <b>Room {i + 1}</b>
                {rooms.length > 1 && (
                  <button type="button" className="link small" onClick={() => onChange(roomsParam(rooms.filter((_, j) => j !== i)))}>Remove</button>
                )}
              </div>
              <Counter label="Adults" hint="18 yrs & above" value={room.adults} min={1} max={MAX_ADULTS} onChange={(n) => update(i, { adults: n })} />
              <Counter label="Children" hint="0 – 17 yrs" value={room.childAges.length} min={0} max={MAX_CHILDREN} onChange={(n) => setChildren(i, n)} />
              {room.childAges.length > 0 && (
                <div className="age-grid">
                  {room.childAges.map((age, k) => (
                    <label key={k} className="age-field">
                      <span>Child {k + 1} age</span>
                      <select value={age} aria-label={`Room ${i + 1} child ${k + 1} age`}
                        onChange={(e) => update(i, { childAges: room.childAges.map((a, x) => (x === k ? Number(e.target.value) : a)) })}>
                        {Array.from({ length: 18 }, (_, a) => <option key={a} value={a}>{a === 0 ? 'Under 1' : a}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
          {rooms.length < MAX_ROOMS && (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => onChange(roomsParam([...rooms, { adults: 2, childAges: [] }]))}>+ Add room</button>
          )}
          <label className="field nat-field">
            <span className="field-label">Lead guest's nationality</span>
            <select value={nat} onChange={(e) => onNat(e.target.value)} aria-label="Lead guest's nationality">
              {countries.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </label>
          <button type="button" className="btn btn-primary block" onClick={() => setOpen(false)}>Apply</button>
        </div>
      )}
    </Segment>
  );
}

/** Choice from [value, label, sub] options, showing the label rather than the value. */
function ChoiceSegment({ label, value, options, onChange, sub }) {
  const { ref, open, setOpen } = usePopover();
  const current = options.find(([v]) => v === value) || options[0];
  return (
    <Segment label={label} innerRef={ref} active={open} onClick={() => setOpen(true)}>
      <span className="seg-value">{current[1]}</span>
      <span className="seg-sub">{sub || current[2]}</span>
      {open && (
        <div className="popover" onClick={(e) => e.stopPropagation()}>
          <ul className="pop-list">
            {options.map(([v, l, s]) => (
              <li key={v} className={v === value ? 'selected' : ''} onClick={() => { onChange(v); setOpen(false); }}>
                <Icon name="calendar" size={18} /><div><b>{l}</b>{s && <span>{s}</span>}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Segment>
  );
}

/** Insurance destinations: regions and/or countries, as the journey allows. Value is a comma list of codes. */
function DestinationSegment({ journey, value, onChange }) {
  const { ref, open, setOpen } = usePopover();
  const [term, setTerm] = useState('');
  const codes = value.split(',').filter(Boolean);
  const showRegions = journey !== 'student';
  const showCountries = journey !== 'annual';
  const needle = term.trim().toLowerCase();
  const regionList = regions.filter(([code, name, sub]) => !needle || `${code} ${name} ${sub}`.toLowerCase().includes(needle));
  const countryList = needle
    ? destinationCountries.filter(([code, name]) => `${code} ${name}`.toLowerCase().includes(needle)).slice(0, 40)
    : popularCountries.map((code) => destinationCountries.find(([c]) => c === code)).filter(Boolean);
  const toggle = (code) => {
    const next = codes.includes(code) ? codes.filter((c) => c !== code) : [...codes, code];
    if (next.length) onChange(next.join(','));
  };
  const first = codes[0] ? destinationName(codes[0]) : 'Choose destination';
  return (
    <Segment label="Travelling to" innerRef={ref} active={open} onClick={() => setOpen(true)} className="seg-dest">
      <span className="seg-value">{first}{codes.length > 1 && <small className="seg-more"> +{codes.length - 1}</small>}</span>
      <span className="seg-sub">{codes.length > 1 ? codes.slice(1).map(destinationName).join(', ') : isRegion(codes[0] || '') ? 'Region' : 'Country'}</span>
      {open && (
        <div className="popover city-pop dest-pop" onClick={(e) => e.stopPropagation()}>
          <div className="dest-chips">
            {codes.map((code) => (
              <button type="button" key={code} className="chip active" onClick={() => toggle(code)} disabled={codes.length === 1}
                aria-label={`Remove ${destinationName(code)}`}>
                {destinationName(code)} {codes.length > 1 && '×'}
              </button>
            ))}
          </div>
          <div className="pop-search">
            <Icon name="search" size={16} />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder={showCountries ? (showRegions ? 'Search region or country' : 'Search country of study') : 'Search region'} />
          </div>
          <ul className="pop-list">
            {showRegions && regionList.length > 0 && <li className="pop-title">Regions</li>}
            {showRegions && regionList.map(([code, name, sub]) => (
              <li key={code} className={codes.includes(code) ? 'selected' : ''} onClick={() => toggle(code)}>
                <Icon name="globe" size={18} /><div><b>{name}</b><span>{sub}</span></div>
                {codes.includes(code) && <Icon name="check" size={16} strokeWidth={2.5} />}
              </li>
            ))}
            {showCountries && countryList.length > 0 && <li className="pop-title">{needle ? 'Countries' : 'Popular countries'}</li>}
            {showCountries && countryList.map(([code, name]) => (
              <li key={code} className={codes.includes(code) ? 'selected' : ''} onClick={() => toggle(code)}>
                <Icon name="pin" size={18} /><div><b>{name}</b><span>{code}</span></div>
                {codes.includes(code) && <Icon name="check" size={16} strokeWidth={2.5} />}
              </li>
            ))}
            {!regionList.length && !countryList.length && <li className="pop-empty">No matching destinations</li>}
          </ul>
          <button type="button" className="btn btn-primary block" onClick={() => { setOpen(false); setTerm(''); }}>Done</button>
        </div>
      )}
    </Segment>
  );
}

/** Insurance travellers: how many, and each one's age (premiums are priced by age). Value is a comma list. */
function AgesSegment({ journey, value, onChange }) {
  const { ref, open, setOpen } = usePopover();
  const ages = value.split(',');
  const { min, max } = ageLimits(journey);
  const setCount = (n) => onChange(Array.from({ length: n }, (_, i) => ages[i] ?? '30').join(','));
  const setAge = (i, age) => onChange(ages.map((a, j) => (j === i ? age : a)).join(','));
  const filled = ages.filter((a) => a.trim() !== '');
  return (
    <Segment label="Travellers" innerRef={ref} active={open} onClick={() => setOpen(true)} className="seg-pax">
      <span className="seg-value">{plural(ages.length, 'Traveller')}</span>
      <span className="seg-sub">{filled.length ? `Age${filled.length > 1 ? 's' : ''} ${filled.join(', ')}` : 'Add ages'}</span>
      {open && (
        <div className="popover pax-pop" onClick={(e) => e.stopPropagation()}>
          <Counter label="Travellers" hint={`Age ${min} – ${max} yrs`} value={ages.length} min={1} max={MAX_TRAVELLERS} onChange={setCount} />
          <div className="pop-title">Age of each traveller</div>
          <div className="age-grid">
            {ages.map((age, i) => (
              <label key={i} className="age-field">
                <span>Traveller {i + 1}</span>
                <input type="number" inputMode="numeric" min={min} max={max} value={age} aria-label={`Traveller ${i + 1} age`}
                  onChange={(e) => setAge(i, e.target.value.replace(/\D/g, '').slice(0, 2))} />
              </label>
            ))}
          </div>
          <p className="muted small">Age in completed years today. It must match each traveller's date of birth at checkout.</p>
          <button type="button" className="btn btn-primary block" onClick={() => setOpen(false)}>Apply</button>
        </div>
      )}
    </Segment>
  );
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function SearchWidget({ initialTab = 'flights', initialValues, compact = false, hideTabs = false }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [values, setValues] = useState(() => {
    const init = { ...defaults[initialTab] };
    Object.entries(initialValues || {}).forEach(([k, val]) => {
      if (k in init) init[k] = numericKeys.includes(k) ? Number(val) || init[k] : val;
    });
    return { ...defaults, [initialTab]: init };
  });
  const [formError, setFormError] = useState('');
  const v = values[tab];
  const set = (k) => (val) => { setFormError(''); setValues((s) => ({ ...s, [tab]: { ...s[tab], [k]: val } })); };
  const setMany = (obj) => { setFormError(''); setValues((s) => ({ ...s, [tab]: { ...s[tab], ...obj } })); };

  // Switching trip type keeps only destinations and ages that type allows.
  const setJourney = (journey) => setMany({
    journey,
    dest: destinationsFor(journey, v.dest.split(',').filter(Boolean)).join(','),
    ages: agesFor(journey, v.ages.split(',').map((a) => Number(a) || 0)).join(','),
  });

  const submit = (e) => {
    e.preventDefault();
    if (tab === 'insurance') {
      if (v.ages.split(',').some((a) => a.trim() === '')) {
        setFormError("Enter each traveller's age.");
        return;
      }
      const search = parseInsuranceQuery(v);
      const problem = searchProblem(search);
      if (problem) {
        setFormError(problem);
        return;
      }
      navigate(`/insurance/results?${new URLSearchParams(toInsuranceParams(search)).toString()}`);
      return;
    }
    if (tab === 'transfers') {
      const search = parseTransferQuery(v);
      const problem = transferProblem(search);
      if (problem) {
        setFormError(problem);
        return;
      }
      navigate(`/transfers/results?${transferQueryString(search)}`);
      return;
    }
    if (tab === 'bus') {
      const search = parseBusQuery(v);
      const problem = busProblem(search);
      if (problem) {
        setFormError(problem);
        return;
      }
      navigate(`/bus/results?${busQueryString(search)}`);
      return;
    }
    if (tab === 'hotels') {
      const search = parseHotelQuery(v);
      const problem = hotelProblem(search);
      if (problem) {
        setFormError(problem);
        return;
      }
      navigate(`/hotels/results?${hotelQueryString(search)}`);
      return;
    }
    const out = { ...v };
    if (tab === 'flights') { out.travellers = v.adults + v.children; if (v.trip === 'oneway') out.returnDate = ''; }
    if (tab === 'packages' || tab === 'sightseeing') out.travellers = v.adults + v.children;
    const params = new URLSearchParams(Object.entries(out).filter(([, x]) => x !== '' && x != null));
    navigate(`/${tab}/results?${params.toString()}`);
  };

  const label = services.find((s) => s.key === tab).label;

  return (
    <div className={`search-card ${compact ? 'compact' : ''}`}>
      {!hideTabs && (
        <div className="search-tabs" role="tablist">
          {services.map((s) => (
            <button key={s.key} type="button" role="tab" aria-selected={tab === s.key}
              className={`search-tab ${tab === s.key ? 'active' : ''}`} onClick={() => setTab(s.key)}>
              <Icon name={s.key} size={26} strokeWidth={1.6} />
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      )}

      <form className="search-form" onSubmit={submit}>
        {tab === 'flights' && (
          <div className="search-options">
            {[['oneway', 'One Way'], ['round', 'Round Trip']].map(([k, l]) => (
              <label key={k} className={`radio ${v.trip === k ? 'active' : ''}`}>
                <input type="radio" name="trip" checked={v.trip === k} onChange={() => set('trip')(k)} /><span className="radio-dot" />{l}
              </label>
            ))}
            {!compact && <span className="options-note">Book international and domestic flights</span>}
          </div>
        )}
        {tab === 'hotels' && !compact && <div className="search-options"><span className="options-note">Book domestic and international hotels, villas & homestays</span></div>}
        {tab === 'packages' && !compact && <div className="search-options"><span className="options-note">Handpicked holidays with flights, stays & sightseeing included</span></div>}
        {tab === 'sightseeing' && !compact && <div className="search-options"><span className="options-note">Tickets, day tours & experiences in 500+ cities</span></div>}
        {tab === 'bus' && !compact && <div className="search-options"><span className="options-note">AC sleepers, Volvo and state transport buses across India</span></div>}
        {tab === 'transfers' && (
          <div className="search-options">
            {transferJourneys.map(([k, l]) => (
              <label key={k} className={`radio ${v.journey === k ? 'active' : ''}`}>
                <input type="radio" name="journey" checked={v.journey === k} onChange={() => set('journey')(k)} /><span className="radio-dot" />{l}
              </label>
            ))}
            {!compact && <span className="options-note">{transferJourneys.find(([k]) => k === v.journey)?.[2]}</span>}
          </div>
        )}
        {tab === 'insurance' && (
          <div className="search-options">
            {journeys.map(([k, l]) => (
              <label key={k} className={`radio ${v.journey === k ? 'active' : ''}`}>
                <input type="radio" name="journey" checked={v.journey === k} onChange={() => setJourney(k)} /><span className="radio-dot" />{l}
              </label>
            ))}
            {!compact && <span className="options-note">{journeys.find(([k]) => k === v.journey)?.[2]}</span>}
          </div>
        )}

        <div className="seg-row">
          {tab === 'flights' && (
            <>
              <CitySegment label="From" value={v.from} onChange={set('from')} extra={
                <button type="button" className="swap-btn" onClick={(e) => { e.stopPropagation(); setMany({ from: v.to, to: v.from }); }} aria-label="Swap cities">
                  <Icon name="swap" size={16} strokeWidth={2.2} />
                </button>
              } />
              <CitySegment label="To" value={v.to} onChange={set('to')} />
              <DateSegment label="Departure" value={v.date} onChange={set('date')} range={v.trip === 'round' ? [v.date, v.returnDate] : null} />
              <DateSegment label="Return" value={v.trip === 'round' ? v.returnDate : ''} min={v.date} range={v.trip === 'round' ? [v.date, v.returnDate] : null}
                emptyText="Add return" onChange={(d) => setMany({ returnDate: d, trip: 'round' })}
                onClear={() => setMany({ returnDate: '', trip: 'oneway' })} />
              <PaxSegment label="Travellers & Class" value={plural(v.adults + v.children + v.infants, 'Traveller')} sub={v.cabin}
                values={v} set={set} cabins={['Economy', 'Premium Economy', 'Business', 'First']}
                counters={[
                  { key: 'adults', label: 'Adults', hint: '12 yrs & above', min: 1, max: 9 },
                  { key: 'children', label: 'Children', hint: '2 – 12 yrs', min: 0, max: 6 },
                  { key: 'infants', label: 'Infants', hint: 'Below 2 yrs', min: 0, max: v.adults },
                ]} />
            </>
          )}

          {tab === 'hotels' && (
            <>
              <HotelDestinationSegment name={v.name} onChange={(d) => setMany(d)} />
              <DateSegment label="Check-in" value={v.checkin} range={[v.checkin, v.checkout]} onChange={(d) => setMany({ checkin: d, checkout: v.checkout <= d ? '' : v.checkout })} />
              <DateSegment label="Check-out" value={v.checkout} min={v.checkin} range={[v.checkin, v.checkout]} emptyText="Select date" onChange={set('checkout')} />
              <RoomsSegment value={v.rooms} nat={v.nat} onChange={set('rooms')} onNat={set('nat')} />
            </>
          )}

          {tab === 'packages' && (
            <>
              <CitySegment label="Starting from" value={v.from} onChange={set('from')} mode="city" />
              <CitySegment label="Going to" value={v.destination} onChange={set('destination')} mode="city" allowAny />
              <DateSegment label="Month of travel" type="month" value={v.month} onChange={set('month')} />
              <PaxSegment label="Travellers" value={plural(v.adults + v.children, 'Traveller')} sub={`${plural(v.adults, 'Adult')}${v.children ? ', ' + v.children + ' Child' : ''}`}
                values={v} set={set}
                counters={[
                  { key: 'adults', label: 'Adults', hint: '12 yrs & above', min: 1, max: 20 },
                  { key: 'children', label: 'Children', hint: '2 – 12 yrs', min: 0, max: 10 },
                ]} />
            </>
          )}

          {tab === 'sightseeing' && (
            <>
              <CitySegment label="Destination" value={v.city} onChange={set('city')} mode="city" allowAny />
              <DateSegment label="Date" value={v.date} onChange={set('date')} />
              <PaxSegment label="Participants" value={plural(v.adults + v.children, 'Person')} sub={`${plural(v.adults, 'Adult')}${v.children ? ', ' + v.children + ' Child' : ''}`}
                values={v} set={set}
                counters={[
                  { key: 'adults', label: 'Adults', hint: '12 yrs & above', min: 1, max: 15 },
                  { key: 'children', label: 'Children', hint: '3 – 12 yrs', min: 0, max: 10 },
                ]} />
            </>
          )}

          {tab === 'transfers' && (
            <>
              <PlaceSegment label="Pickup" name={v.pickup} placeId={v.pickupId} placeholder="Enter pickup"
                onChange={(p) => setMany({ pickup: p.name, pickupId: p.placeId })} />
              <PlaceSegment label="Drop" name={v.drop} placeId={v.dropId} placeholder="Enter drop"
                onChange={(p) => setMany({ drop: p.name, dropId: p.placeId })} />
              <DateSegment label="Pickup date" value={v.date} range={[v.date, v.returnDate]}
                onChange={(d) => setMany({ date: d, returnDate: v.returnDate && v.returnDate < d ? '' : v.returnDate })} />
              <InputSegment label="Pickup time" type="time" value={v.time} onChange={set('time')} sub="Local time at pickup" />
              <DateSegment label="Return" value={v.returnDate} min={v.date} range={[v.date, v.returnDate]} emptyText="Add return"
                onChange={set('returnDate')} onClear={() => set('returnDate')('')} />
              {v.returnDate && <InputSegment label="Return time" type="time" value={v.returnTime} onChange={set('returnTime')} sub="Pickup for the way back" />}
              <PaxSegment label="Passengers" value={plural(v.passengers, 'Passenger')} sub="Incl. children" values={v} set={set}
                counters={[{ key: 'passengers', label: 'Passengers', hint: 'Including children', min: 1, max: MAX_PASSENGERS }]} />
            </>
          )}

          {tab === 'bus' && (
            <>
              <StationSegment label="From" value={v.from} onChange={set('from')} extra={
                <button type="button" className="swap-btn" onClick={(e) => { e.stopPropagation(); setMany({ from: v.to, to: v.from }); }} aria-label="Swap cities">
                  <Icon name="swap" size={16} strokeWidth={2.2} />
                </button>
              } />
              <StationSegment label="To" value={v.to} onChange={set('to')} />
              <DateSegment label="Journey date" value={v.date} onChange={set('date')} />
            </>
          )}

          {tab === 'insurance' && (
            <>
              <DestinationSegment journey={v.journey} value={v.dest} onChange={set('dest')} />
              <DateSegment label={v.journey === 'single' ? 'Trip start' : 'Cover starts'} value={v.start} range={v.journey === 'single' ? [v.start, v.end] : null}
                onChange={(d) => setMany({ start: d, end: v.end < d ? d : v.end })} />
              {v.journey === 'single' && <DateSegment label="Trip end" value={v.end} min={v.start} range={[v.start, v.end]} onChange={set('end')} />}
              {v.journey === 'student' && (
                <ChoiceSegment label="Cover for" value={v.cover} onChange={set('cover')} sub="Length of your course"
                  options={studentCover.map(([k, l]) => [k, l, `${k} days`])} />
              )}
              {v.journey === 'annual' && (
                <ChoiceSegment label="Longest trip" value={v.maxTrip} onChange={set('maxTrip')} sub="Unlimited trips for 1 year"
                  options={annualTripLengths.map(([k, l]) => [k, l, 'Per trip, any number of trips'])} />
              )}
              <AgesSegment journey={v.journey} value={v.ages} onChange={set('ages')} />
            </>
          )}

          {compact && <button type="submit" className="btn btn-primary seg-submit">Search</button>}
        </div>
        {formError && <p className="search-error" role="alert">{formError}</p>}

        {tab === 'flights' && !compact && (
          <div className="fare-types">
            <span className="fare-title">Special fares</span>
            {[['Regular', 'Regular fares'], ['Student', 'Extra baggage'], ['Senior Citizen', 'Up to ₹600 off'], ['Armed Forces', 'Up to ₹600 off'], ['Doctors & Nurses', 'Up to ₹600 off']].map(([k, d]) => (
              <button type="button" key={k} className={`fare-chip ${v.fare === k ? 'active' : ''}`} onClick={() => set('fare')(k)}>
                <b>{k}</b><span>{d}</span>
              </button>
            ))}
          </div>
        )}

        {!compact && <button type="submit" className="btn btn-primary search-btn">Search {label}</button>}
      </form>
    </div>
  );
}
